import { useState, useEffect } from "react";
import axios from "axios";
import UnifiedCourses from "./UnifiedCourses";
import ExamSection from "./ExamSection";
import TranscriptView from "./TranscriptView";
import CertificateView from "./CertificateView";
import CertificateEngine from "./CertificateEngine";
import SubscriptionManagement from "./SubscriptionManagement";
import AdminDashboard from "./AdminDashboard";
import DynamicLessonViewer from "./DynamicLessonViewer";
import StudentProgress from "./StudentProgress";
import OrdinationProgram from "./OrdinationProgram";
import ParentPortal from "./ParentPortal";
import CourseSchedule from "./CourseSchedule";
import ResourceLibrary from "./ResourceLibrary";
import DownloadAppButton from "./DownloadAppButton";
import Leaderboard from "./Leaderboard";
import AttendanceTracker from "./AttendanceTracker";
import ExternalCurriculum from "./ExternalCurriculum";
import { ClassroomIntelligenceProvider } from "./ClassroomIntelligence";
import CourseRecommendations from "./CourseRecommendations";
import CourseCompletionCertificate from "./CourseCompletionCertificate";
import SpecialEdition from "./SpecialEdition";
import EnochianWatchman from "./EnochianWatchman";
import MotionStudioProgressCard from "./MotionStudioProgressCard";
import MySchedule from "./MySchedule";
import VideoLibrary from "./VideoLibrary";
import CelestialDashboard from "./CelestialDashboard";
import ARSkyMap from "./ARSkyMap";
import LiveSkyTest from "./LiveSkyTest";
import K12AcademicProgram from "./K12AcademicProgram";
import K12GradeProgress from "./K12GradeProgress";
import SkyBrief from "./SkyBrief";
import ResumeLessonCard from "./ResumeLessonCard";
import SectionErrorBoundary from "./SectionErrorBoundary";

function Dashboard({ user, onLogout, config, onShowPrivacy }) {
  const [activeSection, setActiveSection] = useState("progress");
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentGradeLevel, setCurrentGradeLevel] = useState("6-8"); // Default grade level
  const [showCertificate, setShowCertificate] = useState(null); // For showing course certificate
  const [isHighSabbath, setIsHighSabbath] = useState(false); // Curriculum lock state
  const [autoOpenForensicEtymology, setAutoOpenForensicEtymology] = useState(false); // For direct Forensic Etymology access
  
  // Check if user is admin (YKBN)
  const isAdmin = user.username === "YKBN" || user.email === "Yisraellearningcenter@gmail.com" || user.is_admin;

  const handleExamComplete = () => {
    // Refresh transcript and user data
    setRefreshKey(prev => prev + 1);
  };

  // Handle privacy policy navigation
  const handlePrivacyClick = () => {
    if (onShowPrivacy) {
      onShowPrivacy();
    } else {
      window.location.href = "/privacy";
    }
  };

  // Calculate trial days remaining
  const trialDaysRemaining = user.trial_days_remaining || 0;
  const isTrialUser = user.subscription_status === "trial";

  // Live subscription window (30-day Stripe cycle). Polled from
  // /api/auth/subscription-status so paid students always see remaining days
  // even after Stripe webhook renewals without needing a re-login.
  const [subStatus, setSubStatus] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const base = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${base}/api/auth/subscription-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setSubStatus(res.data);
      } catch (_) {
        // Silent fail — badge falls back to login-time fields.
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5 * 60 * 1000); // refresh every 5 min
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const subDaysRemaining =
    subStatus?.subscription_days_remaining ?? null;
  const subExpired = subStatus?.subscription_expired === true;

  // Renewal-reminder banner: visible when a PAID student has ≤ 5 days left
  // (or already expired). Dismissible per session so it resurfaces on next
  // visit until they renew. Admin and trial users are exempt.
  const showRenewalBanner =
    !isAdmin &&
    !isTrialUser &&
    (subExpired || (subDaysRemaining !== null && subDaysRemaining <= 5));
  const [renewalDismissed, setRenewalDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("renewal_banner_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const [renewalLoading, setRenewalLoading] = useState(false);

  const handleRenewNow = async () => {
    setRenewalLoading(true);
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("token");
    try {
      // Existing customers → Stripe customer portal (manage / re-add card).
      const portal = await axios.post(
        `${base}/api/subscription/portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (portal?.data?.portal_url) {
        window.location.href = portal.data.portal_url;
        return;
      }
      throw new Error("portal_url missing");
    } catch (_) {
      // Fallback: brand-new customer (no Stripe customer_id yet) → checkout.
      try {
        const co = await axios.post(
          `${base}/api/subscription/create-checkout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (co?.data?.checkout_url || co?.data?.url) {
          window.location.href = co.data.checkout_url || co.data.url;
          return;
        }
      } catch {
        /* swallow — fallback below */
      }
      // Last resort: route to in-app subscription management screen.
      setActiveSection("subscription");
    } finally {
      setRenewalLoading(false);
    }
  };

  const dismissRenewalBanner = () => {
    try {
      sessionStorage.setItem("renewal_banner_dismissed", "1");
    } catch {
      /* ignore */
    }
    setRenewalDismissed(true);
  };

  return (
    <div>
      {/* Trial Warning Banner */}
      {isTrialUser && trialDaysRemaining <= 3 && trialDaysRemaining > 0 && (
        <div style={{
          background: "linear-gradient(90deg, #8B0000 0%, #4d0000 100%)",
          color: "#FFD700",
          padding: "12px 20px",
          textAlign: "center",
          fontWeight: "bold",
          position: "sticky",
          top: 0,
          zIndex: 100
        }} data-testid="trial-warning-banner">
          ⚠️ Your free trial ends in {trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""}! 
          <button 
            onClick={() => setActiveSection("subscription")}
            style={{
              background: "#FFD700",
              color: "#000",
              marginLeft: "15px",
              padding: "5px 15px",
              fontSize: "0.9rem",
              marginTop: 0
            }}
          >
            Subscribe Now
          </button>
        </div>
      )}

      {/* ── PAID-SUBSCRIPTION RENEWAL REMINDER ───────────────────────────────
          Auto-shows for paid students when ≤ 5 days remain in the 30-day Stripe
          billing cycle, or immediately if their cycle already lapsed. One-click
          "Renew Now" opens Stripe customer portal (falls back to checkout for
          new customers). Dismissible per session. */}
      {showRenewalBanner && !renewalDismissed && (
        <div
          data-testid="renewal-reminder-banner"
          style={{
            background: subExpired
              ? "linear-gradient(90deg, #8B0000 0%, #4d0000 100%)"
              : "linear-gradient(90deg, #8B4513 0%, #5a2d0c 100%)",
            color: "#FFD700",
            padding: "12px 20px",
            textAlign: "center",
            fontWeight: "bold",
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <span>
            {subExpired
              ? "⚠ Your subscription has expired. Renew now to continue learning."
              : `⏳ Your subscription renews in ${subDaysRemaining} day${subDaysRemaining !== 1 ? "s" : ""}. Update payment method to avoid interruption.`}
          </span>
          <button
            data-testid="renewal-renew-now-btn"
            onClick={handleRenewNow}
            disabled={renewalLoading}
            style={{
              background: "#FFD700",
              color: "#000",
              padding: "6px 18px",
              fontSize: "0.9rem",
              fontWeight: 700,
              border: "none",
              borderRadius: "6px",
              cursor: renewalLoading ? "wait" : "pointer",
              marginTop: 0,
              opacity: renewalLoading ? 0.7 : 1,
            }}
          >
            {renewalLoading ? "Opening…" : "Renew Now"}
          </button>
          {!subExpired && (
            <button
              data-testid="renewal-dismiss-btn"
              onClick={dismissRenewalBanner}
              aria-label="Dismiss renewal reminder"
              style={{
                background: "transparent",
                color: "#FFD700",
                border: "1px solid #FFD70060",
                padding: "5px 10px",
                fontSize: "0.8rem",
                borderRadius: "6px",
                cursor: "pointer",
                marginTop: 0,
              }}
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      {/* SAFEGUARD: Dashboard Header - Must be CENTERED and industry standard
          DO NOT change to left-align or flex space-between for main content */}
      <header className="app-header no-print" style={{ textAlign: "center" }}>
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center", 
          justifyContent: "center",
          gap: "10px"
        }}>
          <h1 data-testid="dashboard-title" style={{ 
            margin: 0, 
            color: "#FFD700",
            fontSize: "1.8rem",
            textAlign: "center"
          }}>
            Yisrael Learning Center
          </h1>
          <p style={{ margin: "5px 0", color: "#EEEEEE", textAlign: "center" }}>
            {config?.CHURCH_NAME || "Twelve Tribes of Yisrael"} | Established {config?.ESTABLISHED || 2012}
          </p>
          <p style={{ color: "#EEEEEE", textAlign: "center" }}>
            Chief/Priest: {config?.CHIEF_PRIEST || "Ykbn Yoonw"}
          </p>
          <p style={{ fontSize: "0.85rem", marginTop: "5px", color: "#CCCCCC", textAlign: "center" }}>
            K–12 & Advanced Studies • Ministry Training • Cross-Platform Education
          </p>
          <div className="header-download-btn" style={{ marginTop: "10px" }}>
            <DownloadAppButton />
          </div>
        </div>
      </header>

      <div className="container">
        <SkyBrief onJumpToObservatory={() => setActiveSection("celestial")} />
        <ResumeLessonCard
          onResume={(data) => {
            // 2026-06-05 BUG FIX: previously set activeSection="k12",
            // which is NOT a valid section name in this Dashboard
            // (the real name is "k12-program"). The Continue button
            // silently landed on an unrendered section, making it
            // appear as if the button "did nothing". Now we route
            // based on the saved data.section, defaulting to k12-program
            // for K-12 lessons and courses for Track B / Doctrine.
            // The deep-link hint (`ylc_resume_target` localStorage key)
            // is consumed by the destination section to auto-open the
            // exact grade / subject / lesson.
            const savedSection = data?.section;
            const targetSection = (savedSection === "courses")
              ? "courses"
              : "k12-program";
            setActiveSection(targetSection);
          }}
        />
        <div className="card no-print" data-testid="student-info-card">
          <h2>Student Dashboard</h2>
          <p data-testid="welcome-message">
            Welcome, <strong>{user.username}</strong>
            {isAdmin && (
              <span className="status-badge" style={{ background: "#8B0000", color: "#FFD700" }}>
                🔐 Chief Priest Admin
              </span>
            )}
            {isTrialUser ? (
              <span className="status-badge" style={{ 
                background: trialDaysRemaining <= 3 ? "#8B0000" : "#2a4a2a", 
                color: trialDaysRemaining <= 3 ? "#FFD700" : "#90EE90" 
              }} data-testid="trial-status">
                🎁 Trial: {trialDaysRemaining} day{trialDaysRemaining !== 1 ? "s" : ""} remaining
              </span>
            ) : isAdmin ? (
              <span className="status-badge status-active" data-testid="subscription-status">
                ✓ Lifetime Access
              </span>
            ) : (
              <span
                className="status-badge"
                data-testid="subscription-status"
                title={
                  subStatus?.subscription_expires_at
                    ? `Renews / expires on ${new Date(subStatus.subscription_expires_at).toLocaleDateString()}`
                    : "30-day billing cycle"
                }
                style={{
                  background:
                    subExpired
                      ? "#8B0000"
                      : subDaysRemaining !== null && subDaysRemaining <= 3
                      ? "#8B4513"
                      : subDaysRemaining !== null && subDaysRemaining <= 7
                      ? "#2a3a4a"
                      : "#2a4a2a",
                  color:
                    subExpired || (subDaysRemaining !== null && subDaysRemaining <= 3)
                      ? "#FFD700"
                      : "#90EE90",
                }}
              >
                {subExpired
                  ? "⚠ Subscription Expired — Renew"
                  : subDaysRemaining !== null
                  ? `✓ Subscribed · ${subDaysRemaining} day${subDaysRemaining !== 1 ? "s" : ""} remaining`
                  : `✓ Subscribed ($${config?.SUBSCRIPTION_PRICE || 19}.00 USD/month)`}
              </span>
            )}
            {user.ordained && (
              <span className="status-badge status-ordained" data-testid="ordained-badge">
                🎓 Ordained
              </span>
            )}
          </p>
        </div>

        {/* Navigation */}
        <div className="card no-print" data-testid="navigation-card">
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {isAdmin && (
              <button
                data-testid="nav-admin-button"
                onClick={() => setActiveSection("admin")}
                style={{
                  background: activeSection === "admin" ? "#8B0000" : "#333",
                  color: activeSection === "admin" ? "#FFD700" : "#eee",
                  width: "auto",
                  flex: 1,
                  minWidth: "120px",
                  border: activeSection === "admin" ? "2px solid #FFD700" : "none"
                }}
              >
                🔐 Admin
              </button>
            )}
            <button
              data-testid="nav-progress-button"
              onClick={() => setActiveSection("progress")}
              style={{
                background: activeSection === "progress" ? "#FFD700" : "#333",
                color: activeSection === "progress" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              📊 Progress
            </button>
            <button
              data-testid="nav-grade-progress-button"
              onClick={() => setActiveSection("grade-progress")}
              title="K-12 lesson completion, quiz scores & end-of-grade exams per subject"
              style={{
                background: activeSection === "grade-progress"
                  ? "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)"
                  : "#333",
                color: activeSection === "grade-progress" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "140px",
                fontWeight: activeSection === "grade-progress" ? "bold" : "normal"
              }}
            >
              🏆 Grade Progress
            </button>
            <button
              data-testid="nav-courses-button"
              onClick={() => setActiveSection("courses")}
              style={{
                background: activeSection === "courses" ? "#FFD700" : "#333",
                color: activeSection === "courses" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              📚 Courses
            </button>
            <button
              data-testid="nav-k12-program-button"
              onClick={() => setActiveSection("k12-program")}
              style={{
                background: activeSection === "k12-program" 
                  ? "linear-gradient(135deg, #4169E1 0%, #1a3a5c 100%)" 
                  : "#333",
                color: activeSection === "k12-program" ? "#fff" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "160px",
                border: activeSection === "k12-program" ? "2px solid #4169E1" : "none",
                fontWeight: activeSection === "k12-program" ? "bold" : "normal"
              }}
            >
              🎓 K-12 Academic
            </button>
            <button
              data-testid="nav-special-edition-button"
              onClick={() => {
                setAutoOpenForensicEtymology(false);
                setActiveSection("special-edition");
              }}
              style={{
                background: activeSection === "special-edition" 
                  ? "linear-gradient(135deg, #FFD700 0%, #8B0000 100%)" 
                  : "#333",
                color: activeSection === "special-edition" ? "#fff" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "140px",
                border: activeSection === "special-edition" ? "2px solid #FFD700" : "none",
                textShadow: activeSection === "special-edition" ? "0 1px 2px rgba(0,0,0,0.5)" : "none"
              }}
            >
              🌟 Special Edition
            </button>
            <button
              data-testid="nav-celestial-button"
              onClick={() => setActiveSection("celestial")}
              style={{
                background: activeSection === "celestial" 
                  ? "linear-gradient(135deg, #1a1a3a 0%, #2a2a4a 100%)" 
                  : "#333",
                color: activeSection === "celestial" ? "#87CEEB" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "140px",
                border: activeSection === "celestial" ? "2px solid #87CEEB" : "none"
              }}
            >
              🌙 Celestial
            </button>
            <button
              data-testid="nav-exams-button"
              onClick={() => setActiveSection("exams")}
              style={{
                background: activeSection === "exams" ? "#FFD700" : "#333",
                color: activeSection === "exams" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Exams
            </button>
            <button
              data-testid="nav-transcript-button"
              onClick={() => setActiveSection("transcript")}
              style={{
                background: activeSection === "transcript" ? "#FFD700" : "#333",
                color: activeSection === "transcript" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Transcript
            </button>
            <button
              data-testid="nav-my-schedule-button"
              onClick={() => setActiveSection("my-schedule")}
              style={{
                background: activeSection === "my-schedule" ? "#FFD700" : "#333",
                color: activeSection === "my-schedule" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              My Schedule
            </button>
            <button
              data-testid="nav-videos-button"
              onClick={() => setActiveSection("videos")}
              style={{
                background: activeSection === "videos" ? "#FFD700" : "#333",
                color: activeSection === "videos" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              🎬 Videos
            </button>
            <button
              data-testid="nav-certificate-button"
              onClick={() => setActiveSection("certificate")}
              style={{
                background: activeSection === "certificate" ? "#FFD700" : "#333",
                color: activeSection === "certificate" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Certificate
            </button>
            <button
              data-testid="nav-ordination-button"
              onClick={() => setActiveSection("ordination")}
              style={{
                background: activeSection === "ordination" ? "#8B0000" : "#333",
                color: activeSection === "ordination" ? "#FFD700" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px",
                border: activeSection === "ordination" ? "2px solid #FFD700" : "none"
              }}
            >
              ✝️ Ordination
            </button>
            <button
              data-testid="nav-skymap-button"
              onClick={() => setActiveSection("skymap")}
              style={{
                background: activeSection === "skymap" ? "#1a1a3a" : "#333",
                color: activeSection === "skymap" ? "#87CEEB" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px",
                border: activeSection === "skymap" ? "2px solid #87CEEB" : "none"
              }}
            >
              🌌 Sky Map
            </button>
            <button
              data-testid="nav-skytest-button"
              onClick={() => setActiveSection("skytest")}
              style={{
                background: activeSection === "skytest" ? "#2a1a2a" : "#333",
                color: activeSection === "skytest" ? "#FFD700" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px",
                border: activeSection === "skytest" ? "2px solid #FFD700" : "none"
              }}
            >
              🔭 Sky Test
            </button>
            <button
              data-testid="nav-schedule-button"
              onClick={() => setActiveSection("schedule")}
              style={{
                background: activeSection === "schedule" ? "#DDA0DD" : "#333",
                color: activeSection === "schedule" ? "#000" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              📅 Schedule
            </button>
            <button
              data-testid="nav-parent-button"
              onClick={() => setActiveSection("parent")}
              style={{
                background: activeSection === "parent" ? "#87CEEB" : "#333",
                color: activeSection === "parent" ? "#000" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              👨‍👩‍👧 Parent
            </button>
            <button
              data-testid="nav-resources-button"
              onClick={() => setActiveSection("resources")}
              style={{
                background: activeSection === "resources" ? "#96ceb4" : "#333",
                color: activeSection === "resources" ? "#000" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Resources
            </button>
            <button
              data-testid="nav-leaderboard-button"
              onClick={() => setActiveSection("leaderboard")}
              style={{
                background: activeSection === "leaderboard" ? "#FFD700" : "#333",
                color: activeSection === "leaderboard" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Leaderboard
            </button>
            <button
              data-testid="nav-attendance-button"
              onClick={() => setActiveSection("attendance")}
              style={{
                background: activeSection === "attendance" ? "#90EE90" : "#333",
                color: activeSection === "attendance" ? "#000" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Attendance
            </button>
            <button
              data-testid="nav-curriculum-button"
              onClick={() => setActiveSection("curriculum")}
              style={{
                background: activeSection === "curriculum" ? "#87CEEB" : "#333",
                color: activeSection === "curriculum" ? "#000" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Curriculum
            </button>
            <button
              data-testid="nav-subscription-button"
              onClick={() => setActiveSection("subscription")}
              style={{
                background: activeSection === "subscription" ? "#FFD700" : "#333",
                color: activeSection === "subscription" ? "#111" : "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              Subscription
            </button>
            <button
              data-testid="nav-privacy-button"
              onClick={handlePrivacyClick}
              style={{
                background: "#333",
                color: "#eee",
                width: "auto",
                flex: 1,
                minWidth: "120px"
              }}
            >
              🔒 Privacy Policy
            </button>
          </div>
        </div>

        {/* Quick Access: Forensic Etymology Shortcut */}
        {activeSection !== "special-edition" && (
          <div 
            className="card no-print"
            data-testid="quick-access-forensic-etymology"
            onClick={() => {
              setAutoOpenForensicEtymology(true);
              setActiveSection("special-edition");
            }}
            style={{
              background: "linear-gradient(135deg, #1a1a0a 0%, #2a2a1a 100%)",
              borderLeft: "4px solid #FFD700",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "15px 20px",
              transition: "transform 0.2s, box-shadow 0.2s",
              marginBottom: "15px"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(255, 215, 0, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <span style={{ fontSize: "2rem" }}>📜</span>
              <div>
                <div style={{ color: "#FFD700", fontWeight: "bold", fontSize: "1.1rem" }}>
                  Quick Access: Forensic Etymology
                </div>
                <div style={{ color: "#aaa", fontSize: "0.9rem", marginTop: "3px" }}>
                  Stripping Roman Paint to Find Hebrew Copper — Parent Root Etymology Track
                </div>
              </div>
            </div>
            <div style={{
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#000",
              padding: "8px 16px",
              borderRadius: "20px",
              fontSize: "0.85rem",
              fontWeight: "bold",
              whiteSpace: "nowrap"
            }}>
              Start Learning →
            </div>
          </div>
        )}

        {/* Content Sections */}
        {activeSection === "admin" && isAdmin && <AdminDashboard user={user} config={config} />}
        {activeSection === "progress" && (
          <div className="progress-grid" style={{ display: "grid", gap: "20px" }}>
            <div>
              <StudentProgress 
                onNavigateToSubject={(subjectId, gradeLevel) => {
                  // Navigate to courses
                  setActiveSection("courses");
                }}
              />
              
              {/* Course Recommendations */}
              <div style={{ marginTop: "30px" }}>
                <CourseRecommendations 
                  user={user}
                  onSelectCourse={(rec) => {
                    setCurrentGradeLevel(rec.grade_level);
                    setActiveSection("courses");
                  }}
                />
              </div>
            </div>
            
            {/* Enochian Watchman Widget - Right Sidebar */}
            <div>
              <EnochianWatchman 
                compact={false}
                onHighSabbath={(locked) => setIsHighSabbath(locked)}
              />

              {/* Admin-only: live Motion Studio batch monitor */}
              {isAdmin && <MotionStudioProgressCard />}
              
              {/* High Sabbath Notice */}
              {isHighSabbath && (
                <div style={{
                  marginTop: "15px",
                  background: "linear-gradient(135deg, #8B4513 0%, #CD853F 100%)",
                  borderRadius: "12px",
                  padding: "15px",
                  textAlign: "center"
                }}>
                  <div style={{ color: "#fff", fontWeight: "bold", marginBottom: "5px" }}>
                    🕊️ High Sabbath Active
                  </div>
                  <div style={{ color: "#eee", fontSize: "0.85rem" }}>
                    Traditional K-12 lessons are visible but marked &quot;Available after High Sabbath&quot;
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {activeSection === "courses" && (
          <SectionErrorBoundary sectionLabel="Courses" onBack={() => setActiveSection("progress")}>
            <UnifiedCourses 
              onGradeLevelChange={setCurrentGradeLevel}
              onShowCertificate={(subjectId, gradeLevel) => setShowCertificate({ subjectId, gradeLevel })}
              user={user}
              isAdmin={isAdmin}
            />
          </SectionErrorBoundary>
        )}
        {activeSection === "k12-program" && (
          <SectionErrorBoundary sectionLabel="K-12 Program" onBack={() => setActiveSection("progress")}>
            <K12AcademicProgram user={user} />
          </SectionErrorBoundary>
        )}
        {activeSection === "grade-progress" && (
          <K12GradeProgress onBack={() => setActiveSection("progress")} />
        )}
        {activeSection === "special-edition" && (
          <SectionErrorBoundary sectionLabel="Special Edition" onBack={() => setActiveSection("progress")}>
            <SpecialEdition 
              user={user}
              gradeLevel={currentGradeLevel}
              autoOpenForensicEtymology={autoOpenForensicEtymology}
            />
          </SectionErrorBoundary>
        )}
        {activeSection === "celestial" && (
          <SectionErrorBoundary sectionLabel="Celestial Observatory" onBack={() => setActiveSection("progress")}>
            <CelestialDashboard 
              onCurriculumLock={(locked) => setIsHighSabbath(locked)}
              onBack={() => setActiveSection("progress")}
            />
          </SectionErrorBoundary>
        )}
        {activeSection === "exams" && (
          <SectionErrorBoundary sectionLabel="Exams" onBack={() => setActiveSection("progress")}>
            <ExamSection onExamComplete={handleExamComplete} config={config} />
          </SectionErrorBoundary>
        )}
        {activeSection === "transcript" && <TranscriptView key={refreshKey} />}
        {activeSection === "my-schedule" && (
          <SectionErrorBoundary sectionLabel="My Schedule" onBack={() => setActiveSection("progress")}>
            <MySchedule user={user} isAdmin={isAdmin} />
          </SectionErrorBoundary>
        )}
        {activeSection === "videos" && (
          <SectionErrorBoundary sectionLabel="Video Library" onBack={() => setActiveSection("progress")}>
            <VideoLibrary />
          </SectionErrorBoundary>
        )}
        {activeSection === "certificate" && (
          <CertificateEngine user={user} config={config} key={refreshKey} />
        )}
        {activeSection === "ordination" && (
          <SectionErrorBoundary sectionLabel="Ordination Program" onBack={() => setActiveSection("progress")}>
            <OrdinationProgram 
              user={user} 
              config={config} 
              onOrdinationComplete={() => setRefreshKey(prev => prev + 1)}
            />
          </SectionErrorBoundary>
        )}
        {activeSection === "schedule" && (
          <CourseSchedule user={user} config={config} />
        )}
        {activeSection === "parent" && (
          <ParentPortal user={user} config={config} />
        )}
        {activeSection === "resources" && (
          <ResourceLibrary user={user} isAdmin={isAdmin} />
        )}
        {activeSection === "leaderboard" && (
          <Leaderboard token={localStorage.getItem("token")} />
        )}
        {activeSection === "attendance" && (
          <AttendanceTracker user={user} />
        )}
        {activeSection === "curriculum" && (
          <ExternalCurriculum />
        )}
        {activeSection === "subscription" && (
          <SubscriptionManagement config={config} user={user} />
        )}
        {activeSection === "skymap" && (
          <ARSkyMap user={user} />
        )}
        {activeSection === "skytest" && (
          <LiveSkyTest user={user} grade={user?.current_grade || "10"} />
        )}

        {/* Footer with Logout and Privacy */}
        <div className="no-print" style={{ 
          marginTop: "40px",
          paddingTop: "20px",
          borderTop: "1px solid #333",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "15px"
        }}>
          <button data-testid="logout-button" onClick={onLogout} className="btn-secondary">
            Logout
          </button>
          <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
            <button
              onClick={handlePrivacyClick}
              style={{
                background: "transparent",
                border: "none",
                color: "#87CEEB",
                fontSize: "0.85rem",
                cursor: "pointer",
                textDecoration: "underline",
                padding: "5px",
                marginTop: 0
              }}
              data-testid="privacy-policy-link"
            >
              Privacy Policy
            </button>
            <span style={{ color: "#999", fontSize: "0.85rem" }}>
              © {new Date().getFullYear()} {config?.CHURCH_NAME || "Yisrael Learning Center"}
            </span>
          </div>
        </div>
      </div>
      
      {/* Classroom Intelligence AI Sidebar */}
      <ClassroomIntelligenceProvider 
        user={user} 
        gradeLevel={currentGradeLevel}
      >
        {/* Provider wraps nothing here but renders the sidebar via portal */}
        <></>
      </ClassroomIntelligenceProvider>
      
      {/* Course Completion Certificate Modal */}
      {showCertificate && (
        <CourseCompletionCertificate
          user={user}
          subjectId={showCertificate.subjectId}
          gradeLevel={showCertificate.gradeLevel}
          onClose={() => setShowCertificate(null)}
        />
      )}
    </div>
  );
}

export default Dashboard;